<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import CornerFrame from '$lib/components/CornerFrame.svelte';
  import Star from '$lib/components/Star.svelte';
  import ContactCtaSection from '$lib/sections/ContactCtaSection.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';
  import { Home, Brain, Wrench, Cloud, Shield, Eye, Plug, Settings } from '@lucide/svelte';

  // The Full Stack - 4 columns (from existing platform page)
  const stackColumns = [
    {
      title: 'User Surfaces',
      subtitle: 'Where your people interact with AI',
      items: ['The Hub', 'Embeddable widgets', 'Teams & Slack', 'API access'],
      icon: Home
    },
    {
      title: 'Agent Framework',
      subtitle: 'What you build and configure',
      items: ['Visual agent builder', 'Sandbox testing', 'Publish flows', 'MCP & OpenAPI extensibility'],
      icon: Brain
    },
    {
      title: 'Data & Integrations',
      subtitle: 'What your AI connects to',
      items: ['Azure AI Search', 'Database & file connectors', 'Private network access', 'Model-agnostic'],
      icon: Wrench
    },
    {
      title: 'Security & Infrastructure',
      subtitle: 'What governs and runs it',
      items: ['Cumulative Restrictions Framework', 'Full audit trail', 'Entra ID native', 'Your Azure tenant'],
      icon: Shield
    }
  ];

  // Sub-page links
  const subPages = [
    {
      href: '/product/hub',
      title: 'Hub & User Surfaces',
      description: 'Production-ready AI interfaces your team can use immediately. The Hub, embeddable widgets, Teams and Slack integration, and a full API for custom surfaces.',
      icon: Home,
      featured: false
    },
    {
      href: '/product/builder',
      title: 'Agent Builder',
      description: 'Design agents for your workflows. Define permissions, test in sandbox, promote through approval flows. No code required — fully extensible when you need it.',
      icon: Brain,
      featured: false
    },
    {
      href: '/product/security',
      title: 'Security & Governance',
      description: 'Full-stack control over what AI can do — not just what it sees. The Cumulative Restrictions Framework governs agent behavior across every layer. Safe extensibility built into the architecture.',
      icon: Shield,
      featured: true
    },
    {
      href: '/product/connectors',
      title: 'Connectors & Integrations',
      description: 'Wire AI into the systems where your business runs. Secure, governed connections to your data sources, APIs, and internal tools — behind your firewall.',
      icon: Plug,
      featured: false
    },
    {
      href: '/product/admin',
      title: 'Admin & Observability',
      description: 'Every agent, every action, every data flow — visible in real time. Central catalog, role-based access, promotion flows, and cost tracking.',
      icon: Eye,
      featured: false
    }
  ];

  // Value pillars
  const pillars = [
    {
      title: 'Feels like SaaS',
      description: 'Beautiful interfaces. Automatic updates. Zero maintenance burden. Your users love it — and forget it\'s running in your cloud.'
    },
    {
      title: 'Runs in your cloud',
      description: 'Deployed to your Azure tenant. Your data never leaves your boundary. Not by policy — by architecture.'
    },
    {
      title: 'Yours to extend',
      description: 'API-first. MCP and OpenAPI extension points. Override anything. The foundation gets out of your way when you need it to.'
    }
  ];
</script>

<PageMeta
  title="Product — The complete foundation. Yours to shape."
  description="The complete product layer between AI models and the people who need to use them. User surfaces, agent framework, security, connectors, governance — deployed into your cloud."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          The complete foundation. Yours to shape.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          The model is an API key away. Corral is everything else: the user surfaces, the agent framework, the security model, the connectors, the governance layer, and the on-tenant deployment that makes it all enterprise-ready. One foundation, deployed into your cloud, maintained by us, shaped by your team.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider: Hero → Narrative -->
<div class="h-[79px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §1: The Narrative -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <!-- Left: prose -->
        <div class="max-w-[500px]">
          <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-8">
            This isn't a wrapper. It's the product layer.
          </h2>
          <div class="flex flex-col gap-6">
            <p class="font-mono font-light text-[16px] text-[#878787] leading-relaxed">
              Every company deploying AI faces the same reality: authentication, model orchestration, guardrails, tool integration, user interfaces, governance, compliance, deployment architecture. Seven layers, all interdependent.
            </p>
            <p class="font-mono font-light text-[16px] text-white leading-relaxed">
              What makes it different: it's designed to be made yours. Configure it, extend it, or build an entire product on top of it.
            </p>
          </div>
        </div>

        <!-- Right: 4-column stack (stacked 2x2 on this layout) -->
        <div class="grid grid-cols-2 gap-4">
          {#each stackColumns as column}
            <div class="bg-[#171719] border border-[#333335] p-5">
              <div class="flex items-center gap-2.5 mb-3">
                <CornerFrame
                  class="w-[36px] h-[36px] flex items-center justify-center"
                  bg="bg-transparent"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  cornerColor="white"
                  cornerSize={6}
                >
                  <svelte:component this={column.icon} class="w-4 h-4 text-white" />
                </CornerFrame>
                <div>
                  <h3 class="font-sans text-[14px] text-white leading-tight">{column.title}</h3>
                </div>
              </div>
              <p class="font-mono font-light text-[11px] text-[#878787] mb-3">{column.subtitle}</p>
              <ul class="flex flex-col gap-1.5">
                {#each column.items as item}
                  <li class="font-mono font-light text-[12px] text-[#cfcfcf] flex items-center gap-2">
                    <span class="w-1 h-1 bg-[#ee6018] rounded-full shrink-0"></span>
                    {item}
                  </li>
                {/each}
              </ul>
            </div>
          {/each}
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[79px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
  <Star variant="full" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="full" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §2: Sub-page Links — Asymmetric 2+3 grid -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="text-center mb-12">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1]">
          Explore the foundation.
        </h2>
      </div>

      <!-- Row 1: 2 cards (Security featured + Hub) -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {#each subPages.filter(p => p.featured || p.title === 'Hub & User Surfaces') as page}
          <a
            href={page.href}
            class="group bg-[#171719] border border-[#333335] p-6 lg:p-8 hover:border-[#ee6018]/40 transition-colors block"
          >
            <div class="flex items-center gap-3 mb-4">
              <CornerFrame
                class="w-[43px] h-[44px] flex items-center justify-center"
                bg="bg-transparent"
                borderColor="rgba(255, 255, 255, 0.3)"
                cornerColor="white"
                cornerSize={7}
              >
                <svelte:component this={page.icon} class="w-4 h-4 text-white" />
              </CornerFrame>
              <h3 class="font-sans text-[18px] text-white">{page.title}</h3>
            </div>
            <p class="font-mono font-light text-[14px] text-[#878787] leading-relaxed mb-4">
              {page.description}
            </p>
            <span class="font-mono text-[13px] text-[#ee6018] group-hover:text-white transition-colors">
              Learn more →
            </span>
          </a>
        {/each}
      </div>

      <!-- Row 2: 3 cards (Builder, Connectors, Admin) -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        {#each subPages.filter(p => !p.featured && p.title !== 'Hub & User Surfaces') as page}
          <a
            href={page.href}
            class="group bg-[#171719] border border-[#333335] p-6 hover:border-[#ee6018]/40 transition-colors block"
          >
            <div class="flex items-center gap-3 mb-4">
              <CornerFrame
                class="w-[43px] h-[44px] flex items-center justify-center"
                bg="bg-transparent"
                borderColor="rgba(255, 255, 255, 0.3)"
                cornerColor="white"
                cornerSize={7}
              >
                <svelte:component this={page.icon} class="w-4 h-4 text-white" />
              </CornerFrame>
              <h3 class="font-sans text-[16px] text-white">{page.title}</h3>
            </div>
            <p class="font-mono font-light text-[13px] text-[#878787] leading-relaxed mb-4">
              {page.description}
            </p>
            <span class="font-mono text-[13px] text-[#ee6018] group-hover:text-white transition-colors">
              Learn more →
            </span>
          </a>
        {/each}
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[79px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §3: Value Pillars -->
<section class="bg-[#f9f9f4] py-[80px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="text-center mb-12">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-[#020202] tracking-[-0.04em] leading-[1.1]">
          The benefits of SaaS. The control of on-tenant.
        </h2>
      </div>

      <div class="relative">
        <!-- Horizontal lines -->
        <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

        <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

        <div class="grid grid-cols-1 md:grid-cols-3 gap-0">
          {#each pillars as pillar, i}
            <div class="border border-[#D3D3D3] bg-white p-8 {i > 0 ? 'lg:-ml-px' : ''} {i >= 1 ? '-mt-px lg:mt-0' : ''}">
              <h3 class="font-sans text-[20px] text-[#020202] mb-4">{pillar.title}</h3>
              <p class="font-mono font-light text-[14px] text-[#5C5855] leading-relaxed">
                {pillar.description}
              </p>
            </div>
          {/each}
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider: Pillars → CTA -->
<div class="h-[79px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- CTA -->
<ContactCtaSection />
